import { initializeDB } from '../config/db';
import { PO_EDITS_STORE, PURCHASE_ORDER_STORE } from '../config/constants';
import axiosInstance from '../axiosInstance';

class PoEditService {
    constructor() {
        this._syncPromise = null;
        this.autoSyncStarted = false;
        this.onlineHandler = null;
        this.intervalId = null;
        this.maxRetries = 3;
        this.retryDelay = 1000;
    }

    /**
     * Dispatch custom event for UI notifications
     */
    dispatchEvent(eventName, detail = {}) {
        window.dispatchEvent(new CustomEvent(eventName, { detail }));
    }

    /**
     * Save an edit to IndexedDB (offline-first) and update the local PO store.
     *
     * @param {number} purchaseOrderId - The IndexedDB id of the PurchaseOrder record
     * @param {number} externalId      - The SQL Server external ID
     * @param {object} changes         - { field: newValue, ... }
     * @param {object} oldValues       - { field: oldValue, ... }
     */
    async saveEdit(purchaseOrderId, externalId, changes, oldValues) {
        const db = await initializeDB();

        // 1. Save pending edit to poEdits store
        const editRecord = {
            purchaseOrderId,
            externalId,
            changes,
            oldValues,
            status: 'pending',
            editedAt: new Date().toISOString(),
            createdAt: new Date().toISOString(),
        };

        const tx1 = db.transaction(PO_EDITS_STORE, 'readwrite');
        await tx1.objectStore(PO_EDITS_STORE).add(editRecord);
        await tx1.done;

        // 2. Optimistically update the local purchaseOrders store
        const tx2 = db.transaction(PURCHASE_ORDER_STORE, 'readwrite');
        const poStore = tx2.objectStore(PURCHASE_ORDER_STORE);
        const po = await poStore.get(purchaseOrderId);
        if (po) {
            const updated = { ...po, ...changes, _locallyEdited: true };
            await poStore.put(updated);
        }
        await tx2.done;

        console.log(`[PoEdit] Saved pending edit for PO externalId=${externalId}`, changes);

        // 3. If online, try to sync immediately
        if (navigator.onLine) {
            this.syncPendingEdits();
        }

        return editRecord;
    }

    /**
     * Get all pending (unsent) edits from IndexedDB
     */
    async getPendingEdits() {
        const db = await initializeDB();
        const tx = db.transaction(PO_EDITS_STORE, 'readonly');
        const store = tx.objectStore(PO_EDITS_STORE);
        const all = await store.getAll();
        return all.filter(edit => edit.status === 'pending');
    }

    /**
     * Sync all pending edits to the server.
     * Uses Promise-based dedup: if sync is already running, callers await the same promise.
     */
    async syncPendingEdits() {
        if (this._syncPromise) return this._syncPromise;
        if (!navigator.onLine) return;

        this._syncPromise = this._executePendingSync();
        try {
            return await this._syncPromise;
        } finally {
            this._syncPromise = null;
        }
    }

    async _executePendingSync() {
        this.dispatchEvent('poEditSyncStart');

        try {
            const pending = await this.getPendingEdits();
            console.log(`[PoEdit] Syncing ${pending.length} pending edits`);

            for (const edit of pending) {
                await this.syncSingleEdit(edit);
            }

            if (pending.length > 0) {
                this.dispatchEvent('poEditSyncComplete', { count: pending.length });
            }
        } catch (error) {
            console.error('[PoEdit] Sync error:', error);
        }
    }

    /**
     * Sync a single pending edit to the backend
     */
    async syncSingleEdit(edit) {
        try {
            const response = await axiosInstance.patch(
                `/equipment/rental/${edit.externalId}`,
                {
                    changes: edit.changes,
                    editedAt: edit.editedAt,
                    force: false,
                }
            );

            if (response.data.success) {
                // Mark as synced in IndexedDB
                await this.markEditSynced(edit.localId, response.data.record);
                console.log(`[PoEdit] Successfully synced edit ${edit.localId}`);
                return true;
            }
        } catch (error) {
            if (error.response?.status === 409) {
                // Conflict — notify UI
                const conflictData = error.response.data;
                this.dispatchEvent('poEditConflict', {
                    edit,
                    serverChanges: conflictData.changes,
                });
                console.warn(`[PoEdit] Conflict for edit ${edit.localId}`, conflictData);
                return false;
            }

            console.error(`[PoEdit] Failed to sync edit ${edit.localId}:`, error);
            return false;
        }
    }

    /**
     * Force-sync an edit (overwrite server data)
     */
    async forceSyncEdit(edit) {
        try {
            const response = await axiosInstance.patch(
                `/equipment/rental/${edit.externalId}`,
                {
                    changes: edit.changes,
                    editedAt: edit.editedAt,
                    force: true,
                }
            );

            if (response.data.success) {
                await this.markEditSynced(edit.localId, response.data.record);
                console.log(`[PoEdit] Force-synced edit ${edit.localId}`);
                return true;
            }
        } catch (error) {
            console.error(`[PoEdit] Force sync failed for edit ${edit.localId}:`, error);
            return false;
        }
    }

    /**
     * Mark an edit as synced and optionally update the local PO record
     */
    async markEditSynced(localId, serverRecord) {
        const db = await initializeDB();

        // Update edit status
        const tx1 = db.transaction(PO_EDITS_STORE, 'readwrite');
        const editStore = tx1.objectStore(PO_EDITS_STORE);
        const edit = await editStore.get(localId);
        if (edit) {
            edit.status = 'synced';
            edit.syncedAt = new Date().toISOString();
            await editStore.put(edit);
        }
        await tx1.done;

        // Update local PO record with server response if available
        if (serverRecord) {
            const tx2 = db.transaction(PURCHASE_ORDER_STORE, 'readwrite');
            const poStore = tx2.objectStore(PURCHASE_ORDER_STORE);
            const existingPo = await poStore.get(serverRecord.id);
            if (existingPo) {
                const updated = { ...existingPo, ...serverRecord, _locallyEdited: undefined };
                await poStore.put(updated);
            }
            await tx2.done;
        }
    }

    /**
     * Cancel a pending edit (remove from queue, revert local PO)
     */
    async cancelEdit(localId) {
        const db = await initializeDB();

        const tx1 = db.transaction(PO_EDITS_STORE, 'readwrite');
        const editStore = tx1.objectStore(PO_EDITS_STORE);
        const edit = await editStore.get(localId);
        if (edit) {
            // Revert local PO to old values
            const tx2 = db.transaction(PURCHASE_ORDER_STORE, 'readwrite');
            const poStore = tx2.objectStore(PURCHASE_ORDER_STORE);
            const po = await poStore.get(edit.purchaseOrderId);
            if (po && edit.oldValues) {
                const reverted = { ...po, ...edit.oldValues, _locallyEdited: undefined };
                await poStore.put(reverted);
            }
            await tx2.done;

            // Delete the edit record
            await editStore.delete(localId);
        }
        await tx1.done;
    }

    /**
     * Start automatic sync (interval + online listener)
     */
    startAutoSync(intervalMinutes = 5) {
        if (this.autoSyncStarted) {
            console.log('[PoEdit] Auto sync already started');
            return;
        }

        this.autoSyncStarted = true;
        console.log('[PoEdit] Starting auto sync...');

        // Initial sync
        this.syncPendingEdits();

        // Periodic sync
        this.intervalId = setInterval(() => {
            this.syncPendingEdits();
        }, intervalMinutes * 60 * 1000);

        // Sync when back online
        this.onlineHandler = () => {
            console.log('[PoEdit] Back online — syncing pending edits...');
            this.syncPendingEdits();
        };
        window.addEventListener('online', this.onlineHandler);
    }

    /**
     * Stop automatic sync
     */
    stopAutoSync() {
        if (!this.autoSyncStarted) return;

        console.log('[PoEdit] Stopping auto sync...');

        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }

        if (this.onlineHandler) {
            window.removeEventListener('online', this.onlineHandler);
            this.onlineHandler = null;
        }

        this.autoSyncStarted = false;
    }
}

export default new PoEditService();
